import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fieldErrorsOf, getErrorMessage, normalizeError } from '@/core/error-handling/ApiErrorHandler';
import type { ApiError } from '@/core/error-handling/types';
import {
  MASTER_ALL_STATUS,
  MASTER_DEFAULT_PAGE_SIZE,
  MASTER_SEARCH_DEBOUNCE_MS,
} from '../constants/masters.constants';
import type {
  BaseMasterRecord,
  MasterCrud,
  MasterFilterCondition,
  MasterQuery,
} from '../models/baseMaster.types';

/**
 * Every master page's state, in one hook.
 *
 * Paging, sorting, debounced search, the create/edit dialog and the five CRUD
 * calls behave the same on all of them, so a sub-feature's own hook is a thin
 * wrapper over this that supplies its crud client and its empty form.
 */

interface UseMasterTableOptions<TRecord, TPayload> {
  crud: MasterCrud<TRecord, TPayload>;
  /** Rows per page. Defaults to MASTER_DEFAULT_PAGE_SIZE. */
  pageSize?: number;
  /**
   * Initial sort column, if the list should not open in API order.
   *
   * **Masters should leave this unset.** Every master list opens newest-first — latest modified, then
   * latest created, then id — and that ordering is the server's default for a caller who sends no sort.
   * Setting this sends `?sort=` on the very first read, which *overrides* that default: every master did,
   * so every master opened alphabetically and the backend's ordering was never reached.
   *
   * It stays for a list that genuinely opens on something else, and clicking a column header still sorts
   * as it always did — that path sets the sort through `onSort` rather than through this.
   */
  sortField?: string;
  /**
   * Which way that initial sort runs. Ascending unless a master says otherwise.
   *
   * Descending is what a list ordered by a timestamp wants: User Management opens on `createdDate`
   * descending so the newest account is the first row of the first page. Ascending there would bury a
   * just-created user on whatever page their creation date happened to fall.
   *
   * Only read when `sortField` is set -- there is no direction without a column to apply it to.
   */
  sortOrder?: 1 | -1;
  /**
   * Query fields this master has and the others do not -- a parent id filter, a date range.
   *
   * Merged into every list call, and compared by value rather than by identity, so a caller may pass a
   * fresh object literal each render without re-fetching in a loop. Changing one returns to the first
   * page, as the shared filters do.
   */
  extraQuery?: Record<string, string | number | null | undefined>;
}

export interface UseMasterTableResult<TRecord, TPayload> {
  rows: TRecord[];
  total: number;
  loading: boolean;
  /** True while a create, update or delete is in flight. */
  saving: boolean;
  /** Set when a read fails; the page shows it instead of an empty table. */
  error: string | null;

  /** The live query -- pass straight to MasterTable. */
  query: MasterQuery;
  /** Raw search text, so the input stays responsive while debouncing. */
  search: string;
  setSearch: (value: string) => void;
  /** '' is the design's "All Status". */
  status: string;
  setStatus: (value: string) => void;
  /** The filter builder's conditions; empty means unfiltered. */
  filters: readonly MasterFilterCondition[];
  setFilters: (conditions: readonly MasterFilterCondition[]) => void;
  onPage: (page: number, size: number) => void;
  onSort: (field: string, order: 1 | -1 | null) => void;

  /** null while the dialog is closed; the record being edited, or {} for new. */
  editing: TRecord | Record<string, never> | null;
  isNew: boolean;
  openCreate: () => void;
  openEdit: (row: TRecord) => void;
  closeDialog: () => void;

  /** Resolves false when the write failed, so the dialog can stay open. */
  save: (payload: TPayload) => Promise<boolean>;
  /**
   * A write against one row, addressed by id rather than by whatever the
   * dialog happens to be holding.
   *
   * `save` reads its verb from `editing`, which is right for a form and wrong
   * for a row action -- a status toggle fired with no dialog open would POST a
   * copy instead of updating the row.
   */
  updateRow: (id: number, payload: TPayload) => Promise<boolean>;
  /**
   * A create that does not go through the dialog's `editing` state.
   *
   * The definition-driven screen owns its own dialog mode -- view, edit and
   * duplicate are not one flag -- so it addresses the write directly, exactly
   * as `updateRow` does.
   */
  createRow: (payload: TPayload) => Promise<boolean>;
  remove: (row: TRecord) => Promise<boolean>;
  /**
   * The full record behind a list row, from the master's detail endpoint.
   *
   * A listing is a projection: Organization's omits the logo and, until the server carried it, the whole
   * Site Configuration section. Opening View or Edit from the row the table happens to hold therefore showed
   * a form with fields the list never carried left blank — so the dialog reads the record properly instead.
   *
   * Falls back to the row it was given if the read fails, because a dialog that opens with most of the
   * record is better than one that does not open at all. The caller is told through `detailError`.
   */
  loadRecord: (row: TRecord) => Promise<TRecord>;
  /** True while `loadRecord` is in flight. */
  loadingRecord: boolean;
  /** Set when a detail read failed and the list row was used instead. */
  detailError: string | null;
  reload: () => void;
  /** The last write error, for a toast. Cleared on the next attempt. */
  writeError: string | null;
  /** Field-level errors returned by the last failed write. */
  writeFieldErrors: Record<string, string[]> | null;
  /**
   * The same message, readable immediately after the write that produced it.
   *
   * `writeError` is state, so a caller that awaited `remove` still holds the render-time value and
   * cannot see the failure it just caused. A refused delete carries the reason the platform gave —
   * which dependent records block it, and how many — and that reason has to reach the list page.
   */
  lastWriteError: () => string | null;
  /**
   * The whole of the last failure, readable in the same turn — status and structured detail, not just copy.
   *
   * A refused delete is told apart from any other failure by its status, and the records blocking it arrive
   * as `details`. Both are lost by the time they reach `writeError`, which is a string and is state.
   */
  lastWriteFailure: () => ApiError | null;
  /**
   * The last write's per-field messages, keyed as the server names each field (`errors[].field`) -- empty when it
   * named none. Read as `lastWriteError` is, right after the write, so a form can show each under its own field.
   */
  lastWriteFieldErrors: () => Record<string, string>;
  /**
   * What the last successful write answered -- a created record with the code the server gave it -- readable in the
   * same turn; null after a failure. A template's create popup names that code (2026-10-14).
   */
  lastWriteResult: () => unknown;
}

export function useMasterTable<TRecord extends BaseMasterRecord, TPayload>({
  crud,
  pageSize = MASTER_DEFAULT_PAGE_SIZE,
  sortField,
  sortOrder = 1,
  extraQuery,
}: UseMasterTableOptions<TRecord, TPayload>): UseMasterTableResult<TRecord, TPayload> {
  const [rows, setRows] = useState<TRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [writeFieldErrors, setWriteFieldErrors] = useState<Record<string, string[]> | null>(null);
  /** Mirrors writeError for callers that read it in the same turn -- see lastWriteError. */
  const writeErrorRef = useRef<string | null>(null);
  /** The same failure whole, for callers that need its status or its details -- see lastWriteFailure. */
  const writeFailureRef = useRef<ApiError | null>(null);
  /** The same write's per-field messages -- see lastWriteFieldErrors. */
  const writeFieldErrorsRef = useRef<Record<string, string>>({});
  /** The last successful write's answer -- see lastWriteResult. */
  const writeResultRef = useRef<unknown>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatusValue] = useState(MASTER_ALL_STATUS);
  const [filters, setFiltersValue] = useState<readonly MasterFilterCondition[]>([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(pageSize);
  const [sort, setSort] = useState<{ field?: string; order: 1 | -1 | null }>({
    field: sortField,
    order: sortField ? sortOrder : null,
  });

  /* Bumped to re-run the read without changing the query. */
  const [reloadToken, setReloadToken] = useState(0);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /* Typing should not fire a request per keystroke. */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      // A new search starts from the first page, or the results look empty.
      setPage(0);
    }, MASTER_SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [search]);

  /*
    By value, not by identity. A caller writing `extraQuery={{ businessUnitId }}` inline builds a new
    object every render; keying the memo on the object itself would make the list effect re-run each
    time, which is a request loop.
  */
  const extraKey = JSON.stringify(extraQuery ?? {});

  const query = useMemo<MasterQuery>(
    () => ({
      page,
      size,
      search: debouncedSearch,
      status,
      sortField: sort.field,
      sortOrder: sort.order,
      filters,
      /*
        Spread last, so a caller's explicit `extraQuery` wins over anything
        assembled above it -- which is what passing one is for.
      */
      ...(JSON.parse(extraKey) as Record<string, unknown>),
    }),
    [debouncedSearch, extraKey, filters, page, size, sort.field, sort.order, status],
  );

  /* A changed filter starts from the first page, or the results look empty. */
  const firstExtra = useRef(true);
  useEffect(() => {
    if (firstExtra.current) {
      firstExtra.current = false;
      return;
    }
    setPage(0);
  }, [extraKey]);

  /** A new filter starts from the first page, or the results look empty. */
  const setStatus = useCallback((value: string) => {
    setStatusValue(value);
    setPage(0);
  }, []);

  /** Same reasoning: applying or clearing conditions returns to page one. */
  const setFilters = useCallback((conditions: readonly MasterFilterCondition[]) => {
    setFiltersValue(conditions);
    setPage(0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        const result = await crud.list(query);
        if (cancelled || !mounted.current) return;
        setRows(result.rows);
        setTotal(result.total);
        setError(null);
      } catch (err: unknown) {
        if (cancelled || !mounted.current) return;
        setRows([]);
        setTotal(0);
        setError(getErrorMessage(normalizeError(err)));
      } finally {
        if (!cancelled && mounted.current) setLoading(false);
      }
    })();

    /*
      Guards the out-of-order response: paging twice quickly would otherwise
      let the first, slower page overwrite the second.
    */
    return () => {
      cancelled = true;
    };
  }, [crud, query, reloadToken]);

  const [editing, setEditing] = useState<TRecord | Record<string, never> | null>(null);
  const [isNew, setIsNew] = useState(false);

  const reload = useCallback(() => {
    setReloadToken((previous) => previous + 1);
  }, []);

  const openCreate = useCallback(() => {
    setWriteError(null);
    setWriteFieldErrors(null);
    setIsNew(true);
    setEditing({});
  }, []);

  const openEdit = useCallback((row: TRecord) => {
    setWriteError(null);
    setWriteFieldErrors(null);
    setIsNew(false);
    setEditing(row);
  }, []);

  const closeDialog = useCallback(() => {
    setEditing(null);
  }, []);

  /** Runs a write, reporting rather than throwing, and reloads on success. */
  const runWrite = useCallback(
    async (write: () => Promise<unknown>): Promise<boolean> => {
      setSaving(true);
      setWriteError(null);
      setWriteFieldErrors(null);
      writeErrorRef.current = null;
      writeFailureRef.current = null;
      writeFieldErrorsRef.current = {};
      writeResultRef.current = null;
      try {
        writeResultRef.current = await write();
        if (mounted.current) reload();
        return true;
      } catch (err: unknown) {
        const normalized = normalizeError(err);
        const message = getErrorMessage(normalized);
        writeFieldErrorsRef.current = fieldErrorsOf(normalized);
        /*
          The ref is written unconditionally and synchronously, and it is what a caller awaiting this
          can actually read. `writeError` is state: the closure that awaited the write captured the
          previous value and would see null on the turn the write failed. That is why a refused delete
          -- "still has 2 divisions; delete those first" -- had a message the screen could not reach.
        */
        writeErrorRef.current = message;
        writeFailureRef.current = normalized;
        if (mounted.current) {
          setWriteError(message);
          setWriteFieldErrors(normalized.details ?? null);
        }
        return false;
      } finally {
        if (mounted.current) setSaving(false);
      }
    },
    [reload],
  );

  const save = useCallback(
    async (payload: TPayload): Promise<boolean> => {
      // `id` is what decides the verb -- an edit always has one.
      const id = editing && 'id' in editing ? (editing as TRecord).id : null;

      const ok = await runWrite(() =>
        id === null ? crud.create(payload) : crud.update(id, payload),
      );
      if (ok && mounted.current) setEditing(null);
      return ok;
    },
    [crud, editing, runWrite],
  );

  const updateRow = useCallback(
    (id: number, payload: TPayload): Promise<boolean> =>
      runWrite(() => crud.update(id, payload)),
    [crud, runWrite],
  );

  const createRow = useCallback(
    (payload: TPayload): Promise<boolean> => runWrite(() => crud.create(payload)),
    [crud, runWrite],
  );

  const remove = useCallback(
    (row: TRecord): Promise<boolean> => runWrite(() => crud.remove(row.id)),
    [crud, runWrite],
  );

  const [loadingRecord, setLoadingRecord] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const loadRecord = useCallback(
    async (row: TRecord): Promise<TRecord> => {
      setLoadingRecord(true);
      setDetailError(null);
      try {
        const full = await crud.get(row.id);
        return full;
      } catch (err: unknown) {
        if (mounted.current) setDetailError(getErrorMessage(normalizeError(err)));
        /* The list row is a real record, just a narrower one. Better than an empty dialog. */
        return row;
      } finally {
        if (mounted.current) setLoadingRecord(false);
      }
    },
    [crud],
  );

  const onPage = useCallback((nextPage: number, nextSize: number) => {
    setPage(nextPage);
    setSize(nextSize);
  }, []);

  const lastWriteError = useCallback(() => writeErrorRef.current, []);

  const lastWriteFailure = useCallback(() => writeFailureRef.current, []);
  const lastWriteFieldErrors = useCallback(() => writeFieldErrorsRef.current, []);
  const lastWriteResult = useCallback(() => writeResultRef.current, []);

  const onSort = useCallback((field: string, order: 1 | -1 | null) => {
    setSort({ field, order });
    setPage(0);
  }, []);

  return {
    rows,
    total,
    loading,
    saving,
    error,
    query,
    search,
    setSearch,
    status,
    setStatus,
    filters,
    setFilters,
    onPage,
    onSort,
    editing,
    isNew,
    openCreate,
    openEdit,
    closeDialog,
    save,
    updateRow,
    createRow,
    remove,
    loadRecord,
    loadingRecord,
    detailError,
    reload,
    writeError,
    writeFieldErrors,
    lastWriteError,
    lastWriteFailure,
    lastWriteFieldErrors,
    lastWriteResult,
  };
}
